import { useEffect, useState } from "react";
import { X, ChevronUp, ChevronDown } from "lucide-react";
import { useRouter } from "next/router";
import { useToast } from "./Toast";

export default function BetSlip({ selections = [], onRemove, onClear, onPlaceBet, isMobile = false }) {
  const [quota, setQuota] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const router = useRouter();
  const { addToast } = useToast();
  useEffect(() => {
    fetch("/api/predictions/quota", { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then(setQuota).catch(() => {});
  }, []);
  const atLimit = quota && !quota.unlimited && quota.remaining <= 0;
  const hasUnavailable = selections.some((selection) => selection.availabilityError);
  const save = async () => {
    if (!selections.length) return addToast("Choose at least one outcome first.", "error");
    if (quota && !quota.unlimited && selections.length > quota.remaining) {
      return addToast(`You have ${quota.remaining} prediction${quota.remaining === 1 ? "" : "s"} remaining.`, "error");
    }
    const result = await onPlaceBet(selections);
    if (result?.success) {
      setQuota((q) => q && !q.unlimited ? { ...q, used: q.used + selections.length, remaining: q.remaining - selections.length } : q);
      setExpanded(false);
    }
  };
  const content = (
    <div className="prediction-slip-inner">
      <div className="prediction-slip-head">
        <h3>Your predictions</h3>
        <button onClick={onClear} className="slip-clear" data-testid={isMobile ? "button-clear-slip-mobile" : "button-clear-slip"}>Clear all</button>
      </div>
      {quota && !quota.unlimited && <div className={`quota-box ${atLimit ? "quota-limit" : ""}`}>
        <div><strong>{atLimit ? "Quota reached" : "Practice quota"}</strong><span>{quota.used}/{quota.limit}</span></div>
        <small>{atLimit ? "Upgrade to keep making calls." : `${quota.remaining} prediction${quota.remaining === 1 ? "" : "s"} remaining`}</small>
        {atLimit && <button onClick={() => router.push("/subscribe")}>View plans</button>}
      </div>}
      <div className="slip-items">
        {selections.map((s, i) => <div className={`slip-item ${s.availabilityError ? "unavailable" : ""}`} key={`${s.fixtureId}-${i}`} data-testid={`${isMobile ? "mobile-" : ""}selection-${i}`}>
          <div className="slip-item-top"><div className="slip-match">{s.matchLabel}</div><button onClick={() => onRemove(i)} aria-label={`Remove ${s.matchLabel}`} data-testid={`${isMobile ? "button-remove-mobile-" : "button-remove-"}${i}`}><X size={15}/></button></div>
          <span className="your-call">Your call: {s.label}</span>
          {s.availabilityError && <div className="slip-availability-error">{s.availabilityError}</div>}
          {s.skillbotLabel && <div className="skillbot-note">SkillBot: {s.skillbotLabel}{s.skillbotConfidence ? ` · ${s.skillbotConfidence}%` : ""}</div>}
        </div>)}
        {!selections.length && <div className="slip-empty">Your saved calls will appear here.</div>}
      </div>
      <div className="slip-summary"><span>{selections.length} prediction{selections.length === 1 ? "" : "s"} selected</span></div>
      <button className="save-predictions" onClick={save} disabled={!selections.length || atLimit || hasUnavailable} data-testid={isMobile ? "button-save-predictions-mobile" : "button-save-predictions"}>{hasUnavailable ? "Remove unavailable prediction" : "Save my predictions"}</button>
      <p className="slip-note">Saved for virtual practice only. No stake, payout, or cash wagering — just your call compared with SkillBot.</p>
    </div>
  );
  if (isMobile) return selections.length ? <div className="mobile-slip-wrap"><button className="mobile-slip-toggle" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}><span><b>{selections.length}</b> Your predictions</span>{expanded ? <ChevronDown size={18}/> : <ChevronUp size={18}/>}</button>{expanded && <div className="mobile-slip-panel">{content}</div>}</div> : null;
  return <aside className="prediction-slip">{content}</aside>;
}